import React, { useState, useEffect, useRef } from 'react';
import {
  X, Send, Brain, FileText, Plus, Trash2, Clock,
  MessageSquare, BookOpen, Filter, ChevronDown,
  Copy, ExternalLink, AlertTriangle, CheckCircle,
  RefreshCw, Search, Mic, Phone, Mail, StickyNote,
  File, Calendar, User, Upload, FileSignature, DollarSign, ClipboardList
} from 'lucide-react';
import {
  clientIntelligenceService,
  KnowledgeSource,
  ChatMessage,
  IntelligenceChat,
  Evidence,
  ChatFilters,
  SourceType,
  AddSourceInput,
  ChatResponse,
  SearchScope
} from '../../services/clientIntelligenceService';
import { supabase } from '../../services/supabaseService';
import type { Contact } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact;
  projectId?: string;
  projectName?: string;
  defaultScope?: SearchScope;
  showScopeSelector?: boolean;
}

const SOURCE_TYPE_CONFIG: Record<SourceType, { label: string; icon: React.ElementType; color: string }> = {
  fathom_meeting: { label: 'Reunión Fathom', icon: Mic, color: 'text-blue-600 bg-blue-50' },
  elevenlabs_call: { label: 'Llamada ElevenLabs', icon: Phone, color: 'text-green-600 bg-green-50' },
  elevenlabs_whatsapp: { label: 'WhatsApp ElevenLabs', icon: MessageSquare, color: 'text-emerald-600 bg-emerald-50' },
  manual_transcript: { label: 'Transcripción Manual', icon: FileText, color: 'text-purple-600 bg-purple-50' },
  internal_note: { label: 'Nota Interna', icon: StickyNote, color: 'text-yellow-600 bg-yellow-50' },
  email: { label: 'Email', icon: Mail, color: 'text-red-600 bg-red-50' },
  document: { label: 'Documento', icon: File, color: 'text-gray-600 bg-gray-50' },
  contract: { label: 'Contrato', icon: FileSignature, color: 'text-amber-600 bg-amber-50' },
  budget: { label: 'Presupuesto', icon: DollarSign, color: 'text-teal-600 bg-teal-50' },
  proposal: { label: 'Propuesta', icon: ClipboardList, color: 'text-rose-600 bg-rose-50' },
};

const CONFIDENCE_CONFIG = {
  high: { label: 'Alta', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  medium: { label: 'Media', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
  low: { label: 'Baja', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
};

const ClientIntelligencePanel: React.FC<Props> = ({
  isOpen, onClose, contact,
  projectId, projectName, defaultScope, showScopeSelector = true
}) => {
  const [activeTab, setActiveTab] = useState<'chat' | 'sources'>('chat');
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [chats, setChats] = useState<IntelligenceChat[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showAddSource, setShowAddSource] = useState(false);
  const [showFathomImport, setShowFathomImport] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ChatFilters>({});
  const [scope, setScope] = useState<SearchScope>(defaultScope || (projectId ? 'project_with_general' : 'client_all'));
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(projectId);
  const [clientProjects, setClientProjects] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && contact) {
      loadData();
      loadProjects();
    }
  }, [isOpen, contact?.id]);

  useEffect(() => {
    if (isOpen) loadData();
  }, [scope, selectedProjectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadProjects = async () => {
    const { data } = await supabase
      .from('projects')
      .select('id, name')
      .eq('client_id', contact.id)
      .order('name');
    setClientProjects(data || []);
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const pid = selectedProjectId || projectId;
      const [sourcesData, chatsData] = await Promise.all([
        clientIntelligenceService.getSources(contact.id, pid, scope),
        clientIntelligenceService.getChats(contact.id, pid),
      ]);
      setSources(sourcesData);
      setChats(chatsData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadChatMessages = async (chatId: string) => {
    setActiveChatId(chatId);
    const msgs = await clientIntelligenceService.getChatMessages(chatId);
    setMessages(msgs);
  };

  const handleNewChat = () => {
    setActiveChatId(null);
    setMessages([]);
    setQuestion('');
  };

  const handleSend = async () => {
    if (!question.trim() || isSending) return;

    const q = question.trim();
    setQuestion('');
    setIsSending(true);
    setError(null);

    // Optimistic: add user message
    const tempUserMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      chat_id: activeChatId || '',
      role: 'user',
      content: q,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response: ChatResponse = await clientIntelligenceService.sendMessage(
        contact.id, q, activeChatId || undefined, selectedProjectId || projectId, filters, scope
      );

      // Replace temp messages with real ones
      if (response.chat_id && !activeChatId) {
        setActiveChatId(response.chat_id);
      }

      const assistantMsg: ChatMessage = {
        id: `resp-${Date.now()}`,
        chat_id: response.chat_id,
        role: 'assistant',
        content: response.answer,
        confidence: response.confidence,
        evidence: response.evidence,
        tokens_used: response.tokens_used,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Refresh chats list
      const updatedChats = await clientIntelligenceService.getChats(contact.id);
      setChats(updatedChats);

    } catch (err: any) {
      setError(err.message || 'Error procesando la consulta');
      // Remove optimistic message
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    if (!confirm('¿Eliminar esta conversación?')) return;
    await clientIntelligenceService.deleteChat(chatId);
    if (activeChatId === chatId) handleNewChat();
    setChats(prev => prev.filter(c => c.id !== chatId));
  };

  const handleDeleteSource = async (sourceId: string) => {
    if (!confirm('¿Eliminar esta fuente y todos sus fragmentos indexados?')) return;
    await clientIntelligenceService.deleteSource(sourceId);
    setSources(prev => prev.filter(s => s.id !== sourceId));
  };

  const handleReIngest = async (sourceId: string) => {
    try {
      await clientIntelligenceService.ingestSource(sourceId);
      setSources(prev => prev.map(s =>
        s.id === sourceId ? { ...s, ingestion_status: 'processing' as const } : s
      ));
      // Poll for completion
      setTimeout(() => loadData(), 5000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatTime = (seconds: number | null): string => {
    if (seconds == null) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Brain className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {projectName ? `Inteligencia — ${projectName}` : 'Inteligencia del Cliente'}
              </h2>
              <p className="text-sm text-gray-500">{contact.fullName}{contact.company ? ` · ${contact.company}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Scope selector */}
            {showScopeSelector && clientProjects.length > 0 && (
              <ScopeSelector
                scope={scope}
                projects={clientProjects}
                currentProjectId={selectedProjectId}
                onChange={(newScope, newPid) => {
                  setScope(newScope);
                  setSelectedProjectId(newPid);
                }}
              />
            )}
            {/* Tabs */}
            <div className="flex bg-white rounded-lg border border-gray-200 p-1">
              <button
                onClick={() => setActiveTab('chat')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'chat' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MessageSquare className="w-4 h-4 inline mr-1" />
                Chat
              </button>
              <button
                onClick={() => setActiveTab('sources')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'sources' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <BookOpen className="w-4 h-4 inline mr-1" />
                Fuentes ({sources.length})
              </button>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          {activeTab === 'chat' ? (
            <ChatView
              chats={chats}
              activeChatId={activeChatId}
              messages={messages}
              question={question}
              isSending={isSending}
              isLoading={isLoading}
              error={error}
              filters={filters}
              showFilters={showFilters}
              sources={sources}
              messagesEndRef={messagesEndRef}
              inputRef={inputRef}
              onSetQuestion={setQuestion}
              onSend={handleSend}
              onKeyDown={handleKeyDown}
              onNewChat={handleNewChat}
              onSelectChat={loadChatMessages}
              onDeleteChat={handleDeleteChat}
              onToggleFilters={() => setShowFilters(!showFilters)}
              onSetFilters={setFilters}
              onCopy={copyToClipboard}
              formatTime={formatTime}
            />
          ) : (
            <SourcesView
              sources={sources}
              isLoading={isLoading}
              onAddSource={() => setShowAddSource(true)}
              onImportFathom={() => setShowFathomImport(true)}
              onDeleteSource={handleDeleteSource}
              onReIngest={handleReIngest}
              formatTime={formatTime}
            />
          )}
        </div>
      </div>

      {/* Add Source Modal */}
      {showAddSource && (
        <AddSourceModal
          clientId={contact.id}
          onClose={() => setShowAddSource(false)}
          onSaved={() => {
            setShowAddSource(false);
            loadData();
          }}
        />
      )}

      {/* Fathom Import Modal */}
      {showFathomImport && (
        <FathomImportModal
          clientId={contact.id}
          clientEmail={contact.email}
          onClose={() => setShowFathomImport(false)}
          onImported={() => {
            setShowFathomImport(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

// ================================================================
// Chat View
// ================================================================

interface ChatViewProps {
  chats: IntelligenceChat[];
  activeChatId: string | null;
  messages: ChatMessage[];
  question: string;
  isSending: boolean;
  isLoading: boolean;
  error: string | null;
  filters: ChatFilters;
  showFilters: boolean;
  sources: KnowledgeSource[];
  messagesEndRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onSetQuestion: (q: string) => void;
  onSend: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onNewChat: () => void;
  onSelectChat: (id: string) => void;
  onDeleteChat: (id: string) => void;
  onToggleFilters: () => void;
  onSetFilters: (f: ChatFilters) => void;
  onCopy: (text: string) => void;
  formatTime: (s: number | null) => string;
}

const ChatView: React.FC<ChatViewProps> = (props) => {
  return (
    <>
      {/* Chat History Sidebar */}
      <div className="w-64 border-r border-gray-200 flex flex-col bg-gray-50">
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={props.onNewChat}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Nueva consulta</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {props.chats.map(chat => (
            <div
              key={chat.id}
              className={`group flex items-center justify-between p-2 rounded-lg cursor-pointer text-sm transition-colors ${
                props.activeChatId === chat.id
                  ? 'bg-indigo-100 text-indigo-900'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              onClick={() => props.onSelectChat(chat.id)}
            >
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{chat.title}</p>
                <p className="text-xs text-gray-400">{chat.message_count} msgs</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); props.onDeleteChat(chat.id); }}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          {props.chats.length === 0 && !props.isLoading && (
            <p className="text-xs text-gray-400 text-center py-4">No hay conversaciones</p>
          )}
        </div>
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <BookOpen className="w-3 h-3" />
            <span>{props.sources.length} fuentes indexadas</span>
          </div>
        </div>
      </div>

      {/* Chat Main Area */}
      <div className="flex-1 flex flex-col">
        {/* Filters */}
        {props.showFilters && (
          <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center space-x-4">
            <select
              value={props.filters.source_type || ''}
              onChange={(e) => props.onSetFilters({ ...props.filters, source_type: e.target.value as SourceType || undefined })}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1"
            >
              <option value="">Todas las fuentes</option>
              {Object.entries(SOURCE_TYPE_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
            <input
              type="date"
              value={props.filters.date_from || ''}
              onChange={(e) => props.onSetFilters({ ...props.filters, date_from: e.target.value || undefined })}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1"
              placeholder="Desde"
            />
            <input
              type="date"
              value={props.filters.date_to || ''}
              onChange={(e) => props.onSetFilters({ ...props.filters, date_to: e.target.value || undefined })}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1"
              placeholder="Hasta"
            />
            <button
              onClick={() => props.onSetFilters({})}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Limpiar
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {props.messages.length === 0 && !props.isSending && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Brain className="w-16 h-16 text-indigo-200 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">Pregunta sobre este cliente</h3>
              <p className="text-sm text-gray-500 max-w-md mb-6">
                Haz preguntas sobre reuniones, llamadas, conversaciones y notas de {props.sources.length > 0 ? `${props.sources.length} fuentes indexadas` : 'este cliente'}.
              </p>
              {props.sources.length === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                  No hay fuentes indexadas. Añade transcripciones o notas desde la pestaña "Fuentes".
                </p>
              )}
              <div className="mt-4 space-y-2">
                {['¿Qué se discutió en la última reunión?', '¿El cliente mencionó algún problema?', '¿Hay acuerdos pendientes?'].map(q => (
                  <button
                    key={q}
                    onClick={() => { props.onSetQuestion(q); }}
                    className="block w-full text-left px-4 py-2 text-sm text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {props.messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-2xl rounded-br-md px-4 py-3' : 'space-y-2'}`}>
                {msg.role === 'user' ? (
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                ) : (
                  <AssistantMessage msg={msg} onCopy={props.onCopy} formatTime={props.formatTime} />
                )}
              </div>
            </div>
          ))}

          {props.isSending && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span>Buscando en las fuentes...</span>
                </div>
              </div>
            </div>
          )}

          {props.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {props.error}
            </div>
          )}

          <div ref={props.messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="flex items-end space-x-2">
            <button
              onClick={props.onToggleFilters}
              className={`p-2 rounded-lg transition-colors ${props.showFilters ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              title="Filtros"
            >
              <Filter className="w-5 h-5" />
            </button>
            <div className="flex-1 relative">
              <textarea
                ref={props.inputRef}
                value={props.question}
                onChange={(e) => props.onSetQuestion(e.target.value)}
                onKeyDown={props.onKeyDown}
                placeholder="Pregunta sobre este cliente..."
                rows={1}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-sm"
                disabled={props.isSending}
              />
            </div>
            <button
              onClick={props.onSend}
              disabled={!props.question.trim() || props.isSending}
              className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ================================================================
// Assistant Message with Evidence
// ================================================================

const AssistantMessage: React.FC<{
  msg: ChatMessage;
  onCopy: (text: string) => void;
  formatTime: (s: number | null) => string;
}> = ({ msg, onCopy, formatTime }) => {
  const conf = msg.confidence ? CONFIDENCE_CONFIG[msg.confidence] : null;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm">
      <div className="px-4 py-3">
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{msg.content}</p>
      </div>

      {/* Confidence */}
      {conf && (
        <div className="px-4 pb-2">
          <span className={`inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-medium rounded-full ${conf.color}`}>
            <conf.icon className="w-3 h-3" />
            <span>Confianza {conf.label}</span>
          </span>
        </div>
      )}

      {/* Evidence */}
      {msg.evidence && msg.evidence.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Evidencia ({msg.evidence.length})</p>
          {msg.evidence.map((ev, i) => (
            <EvidenceCard key={i} evidence={ev} onCopy={onCopy} formatTime={formatTime} />
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between">
        <span className="text-xs text-gray-400">
          {msg.tokens_used ? `${msg.tokens_used} tokens` : ''}
        </span>
        <button
          onClick={() => onCopy(msg.content)}
          className="text-xs text-gray-400 hover:text-gray-600 flex items-center space-x-1"
        >
          <Copy className="w-3 h-3" />
          <span>Copiar</span>
        </button>
      </div>
    </div>
  );
};

// ================================================================
// Evidence Card
// ================================================================

const EvidenceCard: React.FC<{
  evidence: Evidence;
  onCopy: (text: string) => void;
  formatTime: (s: number | null) => string;
}> = ({ evidence, onCopy, formatTime }) => {
  const typeConfig = SOURCE_TYPE_CONFIG[evidence.source_type as SourceType] || SOURCE_TYPE_CONFIG.document;
  const Icon = typeConfig.icon;

  return (
    <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className={`p-1 rounded ${typeConfig.color}`}>
            <Icon className="w-3 h-3" />
          </div>
          <span className="font-medium text-gray-700">{evidence.source_title}</span>
        </div>
        <button onClick={() => onCopy(evidence.quote)} className="text-gray-400 hover:text-gray-600">
          <Copy className="w-3 h-3" />
        </button>
      </div>
      <div className="flex items-center space-x-3 text-gray-500">
        {evidence.date && (
          <span className="flex items-center space-x-1">
            <Calendar className="w-3 h-3" />
            <span>{new Date(evidence.date).toLocaleDateString('es-ES')}</span>
          </span>
        )}
        {evidence.speaker && (
          <span className="flex items-center space-x-1">
            <User className="w-3 h-3" />
            <span>{evidence.speaker}</span>
          </span>
        )}
        {evidence.start_time_seconds != null && (
          <span className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{formatTime(evidence.start_time_seconds)}</span>
          </span>
        )}
      </div>
      {evidence.quote && (
        <blockquote className="border-l-2 border-indigo-300 pl-2 text-gray-600 italic">
          "{evidence.quote}"
        </blockquote>
      )}
    </div>
  );
};

// ================================================================
// Sources View
// ================================================================

const SourcesView: React.FC<{
  sources: KnowledgeSource[];
  isLoading: boolean;
  onAddSource: () => void;
  onImportFathom: () => void;
  onDeleteSource: (id: string) => void;
  onReIngest: (id: string) => void;
  formatTime: (s: number | null) => string;
}> = ({ sources, isLoading, onAddSource, onImportFathom, onDeleteSource, onReIngest }) => {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Fuentes de Conocimiento</h3>
          <p className="text-sm text-gray-500">Transcripciones, notas y documentos indexados para este cliente</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onImportFathom}
            className="flex items-center space-x-2 px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 text-sm transition-colors"
          >
            <Mic className="w-4 h-4" />
            <span>Importar de Fathom</span>
          </button>
          <button
            onClick={onAddSource}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Añadir Fuente</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : sources.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-700 mb-2">Sin fuentes</h4>
          <p className="text-sm text-gray-500 mb-4">
            Añade transcripciones de reuniones, llamadas o notas internas para alimentar la inteligencia del cliente.
          </p>
          <button
            onClick={onAddSource}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
          >
            Añadir primera fuente
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map(source => {
            const typeConfig = SOURCE_TYPE_CONFIG[source.source_type as SourceType] || SOURCE_TYPE_CONFIG.document;
            const Icon = typeConfig.icon;

            return (
              <div key={source.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{source.title}</h4>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                        <span>{typeConfig.label}</span>
                        {source.meeting_date && (
                          <span className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(source.meeting_date).toLocaleDateString('es-ES')}</span>
                          </span>
                        )}
                        <span>{source.chunk_count} fragmentos</span>
                        <span>{(source.raw_text?.length || 0).toLocaleString()} caracteres</span>
                      </div>
                      {source.summary && (
                        <p className="text-xs text-gray-600 mt-2 line-clamp-2">{source.summary}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-3">
                    {/* Status badge */}
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                      source.ingestion_status === 'completed' ? 'bg-green-100 text-green-800' :
                      source.ingestion_status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      source.ingestion_status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {source.ingestion_status === 'completed' ? 'Indexado' :
                       source.ingestion_status === 'processing' ? 'Procesando...' :
                       source.ingestion_status === 'failed' ? 'Error' :
                       'Pendiente'}
                    </span>
                    {(source.ingestion_status === 'failed' || source.ingestion_status === 'pending') && (
                      <button onClick={() => onReIngest(source.id)} className="p-1 text-blue-600 hover:text-blue-800" title="Re-indexar">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => onDeleteSource(source.id)} className="p-1 text-red-400 hover:text-red-600" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {source.ingestion_error && (
                  <p className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">{source.ingestion_error}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ================================================================
// Add Source Modal
// ================================================================

const ACCEPTED_FILE_TYPES = '.pdf,.txt,.md,.csv,.doc,.docx';

const AddSourceModal: React.FC<{
  clientId: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ clientId, onClose, onSaved }) => {
  const [inputMode, setInputMode] = useState<'text' | 'file'>('text');
  const [form, setForm] = useState<AddSourceInput>({
    client_id: clientId,
    source_type: 'manual_transcript',
    title: '',
    raw_text: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    if (!form.title) {
      setForm(prev => ({ ...prev, title: file.name.replace(/\.[^.]+$/, '') }));
    }

    // Auto-detect source type from file name
    const name = file.name.toLowerCase();
    if (name.includes('contrato') || name.includes('contract')) {
      setForm(prev => ({ ...prev, source_type: 'contract' }));
    } else if (name.includes('presupuesto') || name.includes('budget') || name.includes('cotizacion')) {
      setForm(prev => ({ ...prev, source_type: 'budget' }));
    } else if (name.includes('propuesta') || name.includes('proposal')) {
      setForm(prev => ({ ...prev, source_type: 'proposal' }));
    } else {
      setForm(prev => ({ ...prev, source_type: 'document' }));
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('El título es obligatorio'); return; }

    if (inputMode === 'file') {
      if (!selectedFile) { setError('Selecciona un archivo'); return; }
      if (selectedFile.size > 20 * 1024 * 1024) { setError('El archivo no puede superar 20MB'); return; }

      setIsSaving(true);
      setError(null);
      try {
        await clientIntelligenceService.addFileSource(
          clientId,
          selectedFile,
          form.source_type,
          form.title,
          form.project_id,
          form.meeting_date
        );
        onSaved();
      } catch (err: any) {
        setError(err.message || 'Error al procesar el archivo');
      } finally {
        setIsSaving(false);
      }
    } else {
      if (!form.raw_text.trim() || form.raw_text.trim().length < 50) {
        setError('El texto debe tener al menos 50 caracteres');
        return;
      }

      setIsSaving(true);
      setError(null);
      try {
        await clientIntelligenceService.addAndIngest(form);
        onSaved();
      } catch (err: any) {
        setError(err.message || 'Error al guardar la fuente');
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Añadir Fuente de Conocimiento</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
          )}

          {/* Input mode toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setInputMode('text')}
              className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                inputMode === 'text' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Pegar texto</span>
            </button>
            <button
              onClick={() => setInputMode('file')}
              className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                inputMode === 'file' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Upload className="w-4 h-4" />
              <span>Subir archivo</span>
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Ej: Contrato de servicios Acme Corp"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de fuente *</label>
              <select
                value={form.source_type}
                onChange={(e) => setForm({ ...form, source_type: e.target.value as SourceType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {Object.entries(SOURCE_TYPE_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
              <input
                type="datetime-local"
                value={form.meeting_date || ''}
                onChange={(e) => setForm({ ...form, meeting_date: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {inputMode === 'file' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Archivo * <span className="text-xs text-gray-400 font-normal">PDF, TXT, DOCX, CSV (max 20MB)</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleFileChange}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
              >
                {selectedFile ? (
                  <div className="space-y-2">
                    <File className="w-10 h-10 text-indigo-500 mx-auto" />
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                    <p className="text-xs text-indigo-600">Clic para cambiar</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-600">Haz clic o arrastra un archivo aquí</p>
                    <p className="text-xs text-gray-400">Contratos, presupuestos, transcripciones...</p>
                  </div>
                )}
              </div>
              {selectedFile && selectedFile.type === 'application/pdf' && (
                <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg mt-2">
                  El texto del PDF se extraerá automáticamente con IA. Esto puede tardar unos segundos.
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texto completo * <span className="text-xs text-gray-400 font-normal">Min. 50 caracteres</span>
              </label>
              <textarea
                value={form.raw_text}
                onChange={(e) => setForm({ ...form, raw_text: e.target.value })}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm font-mono"
                placeholder="Pega aquí la transcripción, notas o contenido...&#10;&#10;Formato soportado:&#10;[00:01:30] Juan: Hola, bienvenidos a la reunión...&#10;[00:02:15] María: Gracias, empezamos con el punto 1..."
              />
              <p className="text-xs text-gray-400 mt-1">{form.raw_text.length.toLocaleString()} caracteres</p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{inputMode === 'file' ? 'Procesando archivo...' : 'Guardando e indexando...'}</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Guardar e Indexar</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// Fathom Import Modal
// ================================================================

// ================================================================
// Scope Selector
// ================================================================
const ScopeSelector: React.FC<{
  scope: SearchScope;
  projects: { id: string; name: string }[];
  currentProjectId?: string;
  onChange: (scope: SearchScope, projectId?: string) => void;
}> = ({ scope, projects, currentProjectId, onChange }) => {
  const handleChange = (value: string) => {
    if (value === 'client_all' || value === 'client_general') {
      onChange(value, undefined);
    } else if (value.startsWith('po:')) {
      onChange('project_only', value.slice(3));
    } else if (value.startsWith('pg:')) {
      onChange('project_with_general', value.slice(3));
    }
  };

  const currentValue =
    scope === 'client_all' ? 'client_all' :
    scope === 'client_general' ? 'client_general' :
    scope === 'project_only' ? `po:${currentProjectId}` :
    scope === 'project_with_general' ? `pg:${currentProjectId}` :
    'client_all';

  return (
    <select
      value={currentValue}
      onChange={(e) => handleChange(e.target.value)}
      className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-500 max-w-[220px]"
    >
      <option value="client_all">Todo el cliente</option>
      <option value="client_general">Solo contexto general</option>
      {projects.map(p => (
        <option key={`po-${p.id}`} value={`po:${p.id}`}>
          Solo: {p.name}
        </option>
      ))}
      {projects.map(p => (
        <option key={`pg-${p.id}`} value={`pg:${p.id}`}>
          {p.name} + General
        </option>
      ))}
    </select>
  );
};

const FathomImportModal: React.FC<{
  clientId: string;
  clientEmail: string;
  onClose: () => void;
  onImported: () => void;
}> = ({ clientId, clientEmail, onClose, onImported }) => {
  const [meetings, setMeetings] = useState<any[]>([]);
  const [linkedIds, setLinkedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);

  useEffect(() => {
    loadMeetings();
  }, []);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const { fathomService } = await import('../../services/fathomService');
      if (!fathomService.isConfigured()) {
        setError('Fathom no está configurado. Ve a la sección Fathom > Settings.');
        setLoading(false);
        return;
      }

      const [allMeetings, linked] = await Promise.all([
        fathomService.getMeetings(),
        clientIntelligenceService.getLinkedRecordingIds(),
      ]);

      setLinkedIds(linked);

      // Sort: client's meetings first (by email match), then the rest
      const clientMeetings = allMeetings.filter(m =>
        m.calendar_invitees?.some((i: any) =>
          i.email?.toLowerCase() === clientEmail?.toLowerCase()
        )
      );
      const otherMeetings = allMeetings.filter(m =>
        !clientMeetings.includes(m)
      );

      setMeetings([...clientMeetings, ...otherMeetings]);
    } catch (err: any) {
      setError(err.message || 'Error cargando reuniones de Fathom');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (meeting: any) => {
    setImporting(meeting.recording_id);
    setError(null);
    try {
      await clientIntelligenceService.linkFathomMeeting(clientId, meeting);
      setLinkedIds(prev => new Set([...prev, meeting.recording_id]));
      setSuccessId(meeting.recording_id);
      setTimeout(() => setSuccessId(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Error importando reunión');
    } finally {
      setImporting(null);
    }
  };

  const isClientMeeting = (meeting: any) =>
    meeting.calendar_invitees?.some((i: any) =>
      i.email?.toLowerCase() === clientEmail?.toLowerCase()
    );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Importar Reunión de Fathom</h3>
            <p className="text-sm text-gray-500">Selecciona una reunión para vincular con este cliente</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-sm text-gray-500">Cargando reuniones de Fathom...</span>
            </div>
          ) : meetings.length === 0 ? (
            <div className="text-center py-12">
              <Mic className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No hay reuniones disponibles en Fathom</p>
            </div>
          ) : (
            meetings.map(meeting => {
              const isLinked = linkedIds.has(meeting.recording_id);
              const isMatch = isClientMeeting(meeting);
              const isSuccess = successId === meeting.recording_id;

              return (
                <div
                  key={meeting.recording_id}
                  className={`border rounded-lg p-3 transition-colors ${
                    isMatch ? 'border-indigo-200 bg-indigo-50/50' : 'border-gray-200'
                  } ${isSuccess ? 'border-green-300 bg-green-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {meeting.title || meeting.meeting_title || 'Sin título'}
                        </p>
                        {isMatch && (
                          <span className="flex-shrink-0 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
                            Email coincide
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                        <span>
                          {meeting.recording_start_time
                            ? new Date(meeting.recording_start_time).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '-'}
                        </span>
                        <span>{meeting.calendar_invitees?.length || 0} participantes</span>
                        {meeting.recorded_by?.name && (
                          <span>por {meeting.recorded_by.name}</span>
                        )}
                      </div>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      {isSuccess ? (
                        <span className="flex items-center space-x-1 text-xs text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>Importado</span>
                        </span>
                      ) : isLinked ? (
                        <span className="flex items-center space-x-1 text-xs text-gray-400">
                          <CheckCircle className="w-4 h-4" />
                          <span>Ya vinculado</span>
                        </span>
                      ) : (
                        <button
                          onClick={() => handleImport(meeting)}
                          disabled={importing !== null}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {importing === meeting.recording_id ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          ) : (
                            <Plus className="w-3 h-3" />
                          )}
                          <span>Importar</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-between items-center">
          <p className="text-xs text-gray-500">
            Las reuniones donde el email del cliente coincide aparecen primero
          </p>
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientIntelligencePanel;
